import { Component, OnInit, OnDestroy, ViewChild, AfterViewInit, ChangeDetectorRef } from '@angular/core';
import { ActivatedRoute, Data, Router } from '@angular/router';
import { Location } from '@angular/common';
import { Title } from '@angular/platform-browser';
import { TranslateService } from '@ngx-translate/core';
import { Group, Client, Game, Course } from 'core/models';
import { environment } from 'environments/environment';
import {
  BreadcrumbService,
  GamesService,
  GroupsService,
  LicensesService,
  NotificationsService,
  PopUpService,
  ProfileService,
  UtilsService
} from 'core/services';
import { ModalComponent } from 'core/components/modal/modal.component';
import { QuestionBase, DatetimeQuestion } from 'core/components/forms';
import { MatTabChangeEvent, MatTabGroup } from '@angular/material/tabs';
import { StudentsScore } from '@models/students-score';
import { Observable, Subscription } from 'rxjs';
import { tap } from 'rxjs/operators';
import { DateTime } from 'luxon';

@Component({
  selector: 'lms-group',
  templateUrl: './group.component.html',
  styleUrls: ['./group.component.scss']
})
export class GroupComponent implements OnInit, OnDestroy, AfterViewInit {
  public group: Group;
  public client: Client;
  public course: Course;
  public game: Game;
  public now = Date.now();
  public supervisorUrl = '';
  public tabsManager: any | Subscription = new Subscription();
  public studentsScore: StudentsScore;
  @ViewChild('tabGroup', { static: false }) public tabGroup: MatTabGroup;
  @ViewChild('closeGroupModal', { static: false }) public closeGroupModal: ModalComponent;
  private routeSubscription: Subscription = new Subscription();
  private groupStudentScoreSubscription: Subscription = new Subscription();
  public closeGroupQuestions: QuestionBase<any>[];
  public isStudentScoreTab = true;

  constructor(
    public route: ActivatedRoute,
    public router: Router,
    public location: Location,
    public translate: TranslateService,
    public titleService: Title,
    public breadcrumb: BreadcrumbService,
    public profileService: ProfileService,
    public popupService: PopUpService,
    public gamesService: GamesService,
    public notificationsService: NotificationsService,
    public groupsService: GroupsService,
    public licensesService: LicensesService,
    private cd: ChangeDetectorRef
  ) {}

  public ngOnInit(): void {
    this.routeSubscription = this.routeData$().subscribe();
  }

  /**
   * Observable of the selected route
   * @private
   * @return Stream of the data route
   */
  private routeData$(): Observable<Data> {
    return this.route.data.pipe(
      tap((data: Data) => {
        this.group = data['group'];
        this.client = data['client'];
        this.game = data['game'];
        this.course = data['course'];
        if (this.group) {
          this.titleService.setTitle(
            this.group.name +
              ' - ' +
              this.translate.instant('pageTitle.groups') +
              ' - ' +
              this.translate.instant('pageTitle.gamelearn')
          );

          if (this.group.hasBonus) {
            this.supervisorUrl = `${environment.SUPERVISOR_URL}/login?token=${encodeURIComponent(
              btoa(this.getSupervisorEmail())
            )}`;
          }
        }
      }),
      tap((data: Data) => {
        if (data['groupStudentsScore']) {
          this.studentsScore = data['groupStudentsScore']
            ? { ...data['groupStudentsScore'][0], tooltip: data['groupStudentsScore'][1] }
            : data['groupStudentsScore'];

          this.isStudentScoreTab =
            this.studentsScore && this.studentsScore.columns && this.studentsScore.columns.length > 1;
        }
      })
    );
  }

  public ngAfterViewInit(): void {
    this.tabsManager = UtilsService.createTabsManager(this.route, this.tabGroup);
    this.cd.detectChanges();
  }

  public ngOnDestroy() {
    this.routeSubscription.unsubscribe();
    this.groupStudentScoreSubscription.unsubscribe();
    this.tabsManager.unsubscribe();
  }

  public goBack(): void {
    this.location.back();
  }

  public setTab(selectedTab: MatTabChangeEvent) {
    // Score tab
    if (selectedTab.index === 5 && !this.tabsManager.currentTab.origin) {
      this.studentsScore = { ...this.studentsScore, autoRefresh: true };
    }

    this.tabsManager.setTab(selectedTab);
  }

  onGroupDataSaved(group: Group) {
    if (!this.group) {
      this.router.navigate(['/groups', group.id], {
        replaceUrl: true,
        fragment: 'enrollment'
      });
    } else {
      this.group = group;
    }
  }

  onUpdate(group: Group) {
    this.group = group;
  }

  onDelete() {
    this.goBack();
  }

  onUpdateEnrollment(updatedGroup) {
    this.group = updatedGroup;
  }

  /**
   *  Reset the game progress to the initial point of the given license.
   */
  resetGameSupervisor() {
    this.popupService
      .confirmDelete({
        title: this.translate.instant('games.confirmResetGame'),
        confirmButtonText: this.translate.instant('actions.accept')
      })
      .then(() => this.gamesService.resetGame(this.group.gameId, this.getSupervisorEmail()));
  }

  deleteGroup() {
    this.popupService
      .confirmDelete({
        title: this.translate.instant('groups.confirmDelete', {
          group: this.group.name
        }),
        text: this.translate.instant('groups.deleteMessage')
      })
      .then(() => this.groupsService.delete(this.group.id))
      .then(() => this.onDelete())
      .catch((error) => {
        if (error !== 'cancel') {
          const errorLabel = `error.${error.errorCode}`;
          const translatedErrorMessage = this.translate.instant(errorLabel);

          this.popupService.showError({
            title: this.translate.instant('groups.deleteError'),
            text: translatedErrorMessage === errorLabel ? error.serverMessage : translatedErrorMessage
          });
        }
      });
  }

  closeGroup() {
    const options = { groupId: this.group.id, $filter: "status ne 'cancelled'", sort: '-endDate', limit: 1 };
    this.licensesService.fetch(options).then((response) => {
      const license = response && response?.docs?.length ? response.docs[0] : null;
      const endDate =
        license?.endDate && new Date(license.endDate).getTime() > Date.now() ? new Date(license.endDate) : new Date();
      this.closeGroupQuestions = [
        new DatetimeQuestion({
          key: 'endDate',
          label: 'nouns.endDate',
          defaultHour: 23,
          defaultMinute: 59,
          defaultSeconds: 59,
          timezone: this.group.timezone,
          minDate: DateTime.fromJSDate(endDate).startOf('day').toJSDate(),
          value: DateTime.fromJSDate(endDate).endOf('day').toJSDate(),
          validators: { required: true, invalidDate: true }
        })
      ];

      this.closeGroupModal.open();
    });
  }

  confirmCloseGroup(updatedData) {
    const updatedgroup: Group = Object.assign({}, this.group, updatedData);
    this.closeGroupModal.close();
    this.groupsService
      .update(updatedgroup)
      .then((updatedGroup) => {
        this.group = updatedGroup;
      })
      .catch((err) => {
        console.error(err);
      });
  }

  resendBlueMail() {
    this.notificationsService
      .sendBonusGroupCreatedNotification(this.group)
      .then(() => {
        this.popupService.showSuccess({
          title: this.translate.instant('groups.blueMailSent')
        });
      })
      .catch((err) => {
        this.popupService.showError({
          title: this.translate.instant('groups.blueMailError'),
          text: err
        });
      });
  }

  /**
   *  Build the supervisor email as license identification.
   *  @return  {string}  The supervisor email.
   */
  getSupervisorEmail(): string {
    return `group_${this.group.id}@supervisor.gamelearn.net`;
  }

  supervisorUrlOpen() {
    if (this.supervisorUrl) {
      window.open(this.supervisorUrl, '_blank');
    }
  }
}
